import { JudgmentResult, JudgmentResultType, JudgmentResultEnum } from "../../types.client";
import { RsrpTerrestrialMeasure } from "../../../models/schemas/measures/radioWave.schema";
import { RsrpBaseController } from "./RsrpBaseController";
import { RSRP_TERRESTRIAL_THRESHOLDS } from "../../constants";

/**
 * 13. RSRP 지상파 컨트롤러
 *
 * 판정 기준:
 * - SNR ≥ 20dB: 양호
 * - MER ≥ 25dB: 양호
 * - RSSI: -80dBm ~ -20dBm 범위
 */
export class RsrpTerrestrialController extends RsrpBaseController<RsrpTerrestrialMeasure> {
  protected getDefaultData(): RsrpTerrestrialMeasure {
    return {
      snr: "",
      mer: "",
      ber: "",
      rssi: "",
      result: null,
      discussion: "",
    };
  }

  protected judge(): JudgmentResult {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    // 측정값이 하나도 없으면 NA
    if (snr === null && mer === null && rssi === null) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    let result: JudgmentResultType = JudgmentResultEnum.GOOD;

    // SNR 판정 (≥20dB)
    if (snr !== null && snr < RSRP_TERRESTRIAL_THRESHOLDS.snr.min) {
      result = JudgmentResultEnum.BAD;
    }

    // MER 판정 (≥25dB)
    if (mer !== null && mer < RSRP_TERRESTRIAL_THRESHOLDS.mer.min) {
      result = JudgmentResultEnum.BAD;
    }

    // RSSI 판정 (-80dBm ~ -20dBm)
    if (rssi !== null) {
      if (
        rssi < RSRP_TERRESTRIAL_THRESHOLDS.rssi.min ||
        rssi > RSRP_TERRESTRIAL_THRESHOLDS.rssi.max
      ) {
        result = JudgmentResultEnum.BAD;
      }
    }

    return {
      result,
      discussion: this.generateDiscussion(result),
    };
  }

  protected generateDiscussion(result: JudgmentResultType): string {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    if (result === JudgmentResultEnum.NA) {
      return "지상파 RSRP 측정값이 입력되지 않았습니다.";
    }

    const parts: string[] = [];
    if (snr !== null) {
      const snrStatus = snr >= RSRP_TERRESTRIAL_THRESHOLDS.snr.min ? "양호" : "불량";
      parts.push(`SNR ${snr}dB (기준 ≥${RSRP_TERRESTRIAL_THRESHOLDS.snr.min}dB, ${snrStatus})`);
    }
    if (mer !== null) {
      const merStatus = mer >= RSRP_TERRESTRIAL_THRESHOLDS.mer.min ? "양호" : "불량";
      parts.push(`MER ${mer}dB (기준 ≥${RSRP_TERRESTRIAL_THRESHOLDS.mer.min}dB, ${merStatus})`);
    }
    if (rssi !== null) {
      const rssiStatus =
        rssi >= RSRP_TERRESTRIAL_THRESHOLDS.rssi.min && rssi <= RSRP_TERRESTRIAL_THRESHOLDS.rssi.max
          ? "양호"
          : "불량";
      parts.push(
        `RSSI ${rssi}dBm (기준 ${RSRP_TERRESTRIAL_THRESHOLDS.rssi.min}~${RSRP_TERRESTRIAL_THRESHOLDS.rssi.max}dBm, ${rssiStatus})`
      );
    }

    return parts.join(", ");
  }
}
